
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateActivity, useDeleteActivity, type Activity } from "@/hooks/use-activities";
import { useContacts } from "@/hooks/use-contacts";
import { useStaff } from "@/hooks/use-staff";
import { usePartners } from "@/hooks/use-partners";
import { Calendar, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { format } from "date-fns";

const activitySchema = z.object({
  activity_type: z.enum(["Meeting", "Phone Call", "Email Communication", "Proposal Presentation", "Site Visit", "Follow-up", "Other"]),
  activity_date: z.string().min(1, "Activity date is required"),
  title: z.string().min(1, "Title is required"),
  notes: z.string().optional(),
  contact_id: z.string().optional(),
  performed_by_staff_id: z.string().optional(),
  performed_by_partner_id: z.string().optional(),
}).refine((data) => {
  return data.performed_by_staff_id || data.performed_by_partner_id;
}, {
  message: "Must select either a staff member or partner",
  path: ["performed_by_staff_id"]
});

interface ActivityEditModalProps {
  activity: Activity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ActivityEditModal = ({ activity, open, onOpenChange }: ActivityEditModalProps) => {
  const updateActivity = useUpdateActivity();
  const deleteActivity = useDeleteActivity();
  const { data: contacts } = useContacts();
  const { data: staff } = useStaff();
  const { data: partners } = usePartners();

  const form = useForm({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      activity_type: activity.activity_type,
      activity_date: format(new Date(activity.activity_date), "yyyy-MM-dd'T'HH:00"),
      title: activity.title,
      notes: activity.notes || "",
      contact_id: activity.contact_id || undefined,
      performed_by_staff_id: activity.performed_by_staff_id || undefined,
      performed_by_partner_id: activity.performed_by_partner_id || undefined,
    },
  });

  const onSubmit = async (data: any) => {
    const cleanedData = {
      ...data,
      contact_id: data.contact_id || undefined,
      performed_by_staff_id: data.performed_by_staff_id || undefined,
      performed_by_partner_id: data.performed_by_partner_id || undefined,
      notes: data.notes || undefined,
    };

    updateActivity.mutate({ id: activity.id, data: cleanedData }, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handleDelete = () => {
    deleteActivity.mutate(activity.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Edit Activity
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="activity_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Activity Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Phone Call">Phone Call</SelectItem>
                      <SelectItem value="Email Communication">Email Communication</SelectItem>
                      <SelectItem value="Proposal Presentation">Proposal Presentation</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Follow-up">Follow-up</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Initial consultation meeting" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="activity_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Activity Date & Time *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" step="3600" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="contact_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || "none"}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a contact" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No contact selected</SelectItem>
                      {contacts?.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.first_name} {contact.last_name} - {contact.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="performed_by_staff_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Staff Member</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value === "none" ? undefined : value);
                        if (value && value !== "none") form.setValue("performed_by_partner_id", undefined);
                      }} 
                      value={field.value || "none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select staff" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No staff selected</SelectItem>
                        {staff?.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.first_name} {member.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="performed_by_partner_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Partner</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value === "none" ? undefined : value);
                        if (value && value !== "none") form.setValue("performed_by_staff_id", undefined);
                      }} 
                      value={field.value || "none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select partner" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No partner selected</SelectItem>
                        {partners?.map((partner) => (
                          <SelectItem key={partner.id} value={partner.id}>
                            {partner.first_name} {partner.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Activity details, outcomes, follow-up actions..." 
                      className="min-h-[100px]" 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-between space-x-2 pt-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Activity</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this activity? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              <div className="space-x-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateActivity.isPending}>
                  {updateActivity.isPending ? "Updating..." : "Update Activity"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
